import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Pause, RefreshCw, Check, Sparkles, Volume2 } from 'lucide-react';

const PHONETIC_SCRIPT = `Hola, soy Raúl Silveira. Me apasiona la tecnología, el desarrollo de software y la inteligencia artificial para automatizar negocios y multiplicar resultados. Hoy en día, los negocios que no innovan se quedan atrás, y nuestra misión es liderar esa transformación digital con excelencia.`;

export function VoiceRecorderModal({
  isOpen,
  onClose,
  onSaveVoice,
  avatarName = 'Mi Avatar'
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioBlob, setAudioBlob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volumeLevel, setVolumeLevel] = useState(0);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const audioElementRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
    };
  }, []);

  if (!isOpen) return null;

  const startRecording = async () => {
    try {
      audioChunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Audio analysis for VU meter
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      const source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioCtx;
      analyserRef.current = analyser;

      const updateMeter = () => {
        if (!analyserRef.current) return;
        const data = new Uint8Array(analyserRef.current.frequencyBinCount);
        analyserRef.current.getByteFrequencyData(data);
        const avg = data.reduce((a, b) => a + b, 0) / data.length;
        setVolumeLevel(Math.min(100, Math.round((avg / 128) * 100)));
        animFrameRef.current = requestAnimationFrame(updateMeter);
      };
      updateMeter();

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        stream.getTracks().forEach((track) => track.stop());
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        setVolumeLevel(0);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(250);
      setIsRecording(true);
      setRecordingSeconds(0);
      setAudioUrl(null);

      timerIntervalRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Error accediendo al micrófono:', err);
      alert('Por favor autoriza el permiso de micrófono en tu navegador para calibrar tu voz.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }
  };

  const togglePlayback = () => {
    if (!audioUrl) return;
    if (!audioElementRef.current) {
      const audio = new Audio(audioUrl);
      audio.onended = () => setIsPlaying(false);
      audio.play();
      audioElementRef.current = audio;
      setIsPlaying(true);
    } else {
      if (isPlaying) {
        audioElementRef.current.pause();
        setIsPlaying(false);
      } else {
        audioElementRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const handleSave = () => {
    if (audioUrl && onSaveVoice) {
      onSaveVoice(audioUrl, audioBlob);
      onClose();
    }
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(5, 7, 12, 0.85)',
      backdropFilter: 'blur(16px)',
      zIndex: 1100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#111420',
        borderRadius: '16px',
        border: '1px solid rgba(255, 145, 1, 0.3)',
        boxShadow: '0 25px 60px -15px rgba(0,0,0,0.9), 0 0 40px rgba(255,145,1,0.15)',
        width: '100%',
        maxWidth: '560px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '24px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div style={{ background: 'rgba(255,145,1,0.15)', padding: '6px', borderRadius: '8px', color: '#ff9101' }}>
              <Mic size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800, color: '#f8fafc' }}>
                Calibración de Voz en Vivo
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '11px', color: '#94a3b8' }}>
                Perfil para: <strong>{avatarName}</strong>
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '20px', cursor: 'pointer' }}
          >
            ✕
          </button>
        </div>

        {/* Teleprompter Box */}
        <div style={{
          background: '#161926',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.08)',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: '#ff9101', display: 'flex', alignItems: 'center', gap: '5px' }}>
            <Sparkles size={12} /> Lee este texto con tu tono natural y fluido:
          </div>
          <p style={{
            fontSize: '13px',
            color: '#f8fafc',
            lineHeight: '1.6',
            margin: 0,
            fontFamily: 'inherit',
            fontWeight: 500
          }}>
            "{PHONETIC_SCRIPT}"
          </p>
        </div>

        {/* Dynamic VU Meter & Recording Status */}
        <div style={{
          background: '#0d0f17',
          padding: '14px',
          borderRadius: '10px',
          border: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '10px'
        }}>
          <div style={{ fontSize: '22px', fontWeight: 800, color: isRecording ? '#ef4444' : '#f8fafc', fontFamily: 'monospace' }}>
            {formatTime(recordingSeconds)}
          </div>

          {/* VU Meter Bar */}
          <div style={{ width: '100%', height: '8px', background: '#1e2235', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${isRecording ? volumeLevel : 0}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #10b981 0%, #ff9101 70%, #ef4444 100%)',
              transition: 'width 0.08s ease'
            }} />
          </div>

          <div style={{ fontSize: '11px', color: '#64748b' }}>
            {isRecording ? '🎙️ Escuchando y modulando tono...' : audioUrl ? '✅ Muestra capturada. Puedes reproducirla.' : 'Haz clic en Grabar cuando estés listo.'}
          </div>
        </div>

        {/* Action Controls */}
        <div style={{ display: 'flex', gap: '10px' }}>
          {!isRecording && !audioUrl && (
            <button
              type="button"
              onClick={startRecording}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #ff9101 0%, #a855f7 100%)',
                color: '#fff',
                fontWeight: 700,
                fontSize: '13px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Mic size={16} /> Iniciar Grabación
            </button>
          )}

          {isRecording && (
            <button
              type="button"
              onClick={stopRecording}
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '8px',
                background: '#ef4444',
                color: '#fff',
                fontWeight: 700,
                fontSize: '13px',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Square size={16} /> Detener Grabación
            </button>
          )}

          {audioUrl && !isRecording && (
            <>
              <button
                type="button"
                onClick={togglePlayback}
                style={{
                  padding: '12px 18px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#f8fafc',
                  fontWeight: 600,
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                {isPlaying ? 'Pausar' : 'Escuchar'}
              </button>

              <button
                type="button"
                onClick={startRecording}
                style={{
                  padding: '12px 14px',
                  borderRadius: '8px',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#94a3b8',
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Grabar de nuevo"
              >
                <RefreshCw size={16} /> Repetir
              </button>

              <button
                type="button"
                onClick={handleSave}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '13px',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 15px rgba(16,185,129,0.3)'
                }}
              >
                <Check size={16} /> Guardar Voz del Avatar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
